import { type KeyboardEvent, type PointerEvent, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

/** How far one arrow press moves the handle, as a share of the way. */
const STEP = 25;
/** How near the end a drag must be let go to count as the whole way. */
const NEAR_THE_END = 90;

/** Where a drag began: the pointer's x, and the handle's place then. */
interface Grip {
  x: number;
  from: number;
}

/** Where the handle is, and what moves it: the keys and a drag along the track. */
function useSlide({
  fixed,
  confirmed,
  onConfirm,
}: {
  fixed: boolean;
  confirmed: boolean;
  onConfirm: () => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [moved, setMoved] = useState(0);
  const [grip, setGrip] = useState<Grip | null>(null);
  const value = confirmed ? 100 : moved;

  const reach = (to: number) => {
    if (fixed) return;
    if (to >= 100) {
      setMoved(100);
      onConfirm();
      return;
    }
    setMoved(Math.max(0, to));
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const to = new Map([
      ["ArrowRight", value + STEP],
      ["ArrowUp", value + STEP],
      ["ArrowLeft", value - STEP],
      ["ArrowDown", value - STEP],
      ["Home", 0],
      ["End", 100],
      ["Enter", 100],
      [" ", 100],
    ]).get(event.key);
    if (to === undefined) return;
    event.preventDefault();
    reach(to);
  };

  const travel = (handle: Element) =>
    (railRef.current?.getBoundingClientRect().width ?? 0) -
    handle.getBoundingClientRect().width;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (fixed) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setGrip({ x: event.clientX, from: value });
  };
  /** Where the pointer puts the handle, read from the event so a release needs no move before it. */
  const placed = (event: PointerEvent<HTMLDivElement>, held: Grip) => {
    const way = travel(event.currentTarget);
    if (way <= 0) return held.from;
    const share = ((event.clientX - held.x) / way) * 100;
    return Math.min(100, Math.max(0, held.from + share));
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (grip !== null) setMoved(placed(event, grip));
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (grip === null) return;
    setGrip(null);
    if (placed(event, grip) >= NEAR_THE_END) reach(100);
    else setMoved(0);
  };

  return {
    railRef,
    along: value / 100,
    dragging: grip !== null,
    handle: { onKeyDown, onPointerDown, onPointerMove, onPointerUp },
  };
}

/**
 * The yes that takes a deliberate act: the attorney carries the handle along
 * the track to its end. Let go short of the end and it springs back.
 *
 * The handle is a slider, so it takes focus and says how far it is. The right
 * arrow moves it a quarter of the way; Enter, Space and End take it to the end.
 * Once confirmed it stays at the end: the yes is the caller's to take back.
 * `held` keeps the handle in the tab order with its name and lets it move no
 * further, for a yes the page cannot take yet.
 */
export function SlideToConfirm({
  label,
  doneLabel,
  confirmed,
  held = false,
  onConfirm,
  className,
}: {
  /** What the slide does, written on the track and naming the handle. */
  label: string;
  /** What the track says once the handle reaches the end. */
  doneLabel: string;
  confirmed: boolean;
  held?: boolean;
  onConfirm: () => void;
  className?: string;
}) {
  const { railRef, along, dragging, handle } = useSlide({
    fixed: held || confirmed,
    confirmed,
    onConfirm,
  });
  return (
    <div
      ref={railRef}
      className={cn(
        "relative h-14 w-full max-w-xl touch-none overflow-hidden rounded-full bg-primary select-none",
        held && "opacity-60",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 bg-linear-to-r from-brand-mid to-brand-end",
          !dragging && "transition-[width] duration-200",
        )}
        style={{ width: `calc(3.5rem + (100% - 3.5rem) * ${String(along)})` }}
      />
      <p
        role="status"
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center pr-4 pl-14 text-sm font-bold tracking-wide text-primary-foreground",
          !confirmed && along > 0.6 && "opacity-0",
        )}
      >
        {confirmed ? doneLabel : label}
      </p>
      <div
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(along * 100)}
        aria-valuetext={confirmed ? "Confirmed" : "Not confirmed"}
        aria-disabled={held || undefined}
        {...handle}
        className={cn(
          "absolute top-1 grid size-12 place-items-center rounded-full bg-card text-primary shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          held || confirmed ? "cursor-default" : "cursor-grab",
          !dragging && "transition-[left] duration-200",
        )}
        style={{ left: `calc(0.25rem + (100% - 3.5rem) * ${String(along)})` }}
      >
        {confirmed ? <Check aria-hidden /> : <ArrowRight aria-hidden />}
      </div>
    </div>
  );
}
